import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FireFilled } from "@ant-design/icons";
import { MenuCategory, Item } from "../../types/item";
import { itemsBySubcategory } from "../../utils/menu";
import { MenuItemCard } from "../../layouts";
import useViewModel from "./viewModel";
import Skeleton from "./skeleton";
import Tabs from "./tabs";
import {
  TextInput,
  Carousel,
  Flex,
  Text,
  Empty,
  Row,
  Col,
} from "../../components";
import { Tools } from "../../utils";
import { twMerge } from "tailwind-merge";

const Menu = () => {
  const {
    menu,
    loading,
    banners,
    favorites,
    hero,
    getSearchResults,
    markFavorite,
  } = useViewModel();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!banners?.length) return;
    document.body.classList.add("has-menu-hero");
    return () => document.body.classList.remove("has-menu-hero");
  }, [banners]);

  const searchResults = useMemo(
    () => getSearchResults(searchQuery),
    [searchQuery, getSearchResults],
  );

  const renderItem = useCallback(
    (item: Item) => {
      const favorite = (favorites as string[]).includes(item.uuid);
      return (
        <Col xs={24 / 2} sm={24 / 2} md={24 / 2} lg={24 / 3}>
          <MenuItemCard
            {...item}
            favorite={favorite}
            onFavorite={() => markFavorite(item, !favorite)}
          />
        </Col>
      );
    },
    [favorites, markFavorite],
  );

  const renderGrid = (items: Item[]) =>
    !!items.length && (
      <Row
        gutter={[
          { xs: 8, lg: 16 },
          { xs: 8, lg: 16 },
        ]}
      >
        {React.Children.toArray(items.map(renderItem))}
      </Row>
    );

  const renderSubcategory = (
    name: string,
    uuid: string,
    items: Item[],
    isPopular?: boolean,
  ) => (
    <div key={uuid} id={`${uuid}_subcategory`} className="m-20-t">
      {!!name && (
        <Text className="m-20-b" weight={700} level={2}>
          {isPopular && <FireFilled className="text-primary mr-[0.6rem]" />}
          {name}
        </Text>
      )}
      {renderGrid(items)}
    </div>
  );

  const renderCategory = useCallback(
    (category: MenuCategory) => {
      const { ungrouped, grouped } = itemsBySubcategory(category);
      return (
        <div
          style={{ scrollMarginTop: "var(--menu-tabs-height, 6rem)" }}
          id={`${category.uuid}_category`}
        >
          {!!category.imageUrl && (
            <img
              alt=""
              src={Tools.mediaUrl(category.imageUrl)}
              className="category-banner"
            />
          )}
          {!!ungrouped.length && (
            <div className="m-15-t">{renderGrid(ungrouped)}</div>
          )}
          {React.Children.toArray(
            grouped.map(({ subcategory, items }) =>
              renderSubcategory(
                subcategory.name,
                subcategory.uuid,
                items,
                subcategory.isPopular,
              ),
            ),
          )}
        </div>
      );
    },
    [renderItem],
  );

  if (loading) return <Skeleton hasHero={!!banners?.length} />;

  return (
    <div className="bg-background">
      {!!banners?.length && (
        <div className="mt-[-2.4rem] z-[2] relative px-page">
          <Carousel
            arrows
            autoplay
            infinite
            className="menu-hero"
            autoplaySpeed={hero?.imageRotationMs || 5000}
            children={React.Children.toArray(
              banners.map((banner) => {
                const img = <img alt="" src={Tools.mediaUrl(banner.url)} />;
                if (!banner.link) return <div>{img}</div>;
                return (
                  <div>
                    <button
                      type="button"
                      className="block w-full cursor-pointer border-0 bg-transparent p-0"
                      onClick={() => {
                        const href = Tools.resolveLink(banner.link);
                        if (href)
                          window.open(href, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {img}
                    </button>
                  </div>
                );
              }),
            )}
          />
        </div>
      )}
      {!searchQuery && <Tabs menu={menu} />}
      <div
        className={twMerge("px-page pb-[100px]", searchQuery && "pt-[4rem]")}
      >
        <div
          id="menu-search"
          className="scroll-mt-[var(--menu-tabs-height,6rem)]"
        >
          <TextInput
            type="search"
            className="m-20-b mx-auto w-1/2 mobile:w-full"
            placeholder="Search items..."
            onChange={(_) => setSearchQuery(_.target.value)}
          />
        </div>
        {!!searchQuery ? (
          !searchResults.length ? (
            <Empty description={`${searchQuery} not found`} />
          ) : (
            <Row
              gutter={[
                { xs: 8, lg: 16 },
                { xs: 8, lg: 16 },
              ]}
              children={React.Children.toArray(searchResults.map(renderItem))}
            />
          )
        ) : (
          <Flex
            vertical
            gap={"30px"}
            children={React.Children.toArray(menu.map(renderCategory))}
          />
        )}
      </div>
    </div>
  );
};

export default Menu;
